import React from 'react';
import { FaUser, FaEnvelope, FaPhone, FaBuilding, FaIdCard, FaGlobe, FaLanguage, FaDollarSign, FaMapMarkerAlt, FaClock, FaTag } from 'react-icons/fa';

const AccountDetailsRenderer = ({ data }) => {
  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 text-gray-600">
          <FaUser className="text-sm" />
          <span className="text-sm">No account details found</span>
        </div>
      </div>
    );
  }

  const { personalInfo, addresses, message } = data;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className="text-sm text-gray-600 mb-4">
          {message}
        </div>
      )}
      
      {/* Personal Information */}
      {personalInfo && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FaUser className="mr-2 text-blue-500" />
            Account Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <FaUser className="text-gray-400 text-sm" />
                <div>
                  <span className="text-sm text-gray-500">Name</span>
                  <div className="font-medium text-gray-900">{personalInfo.name}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <FaEnvelope className="text-gray-400 text-sm" />
                <div>
                  <span className="text-sm text-gray-500">Email</span>
                  <div className="font-medium text-gray-900">{personalInfo.email}</div>
                </div>
              </div>
              
              {personalInfo.phone && (
                <div className="flex items-center space-x-3">
                  <FaPhone className="text-gray-400 text-sm" />
                  <div>
                    <span className="text-sm text-gray-500">Phone</span>
                    <div className="font-medium text-gray-900">{personalInfo.phone}</div>
                  </div>
                </div>
              )}
              
              {personalInfo.company && (
                <div className="flex items-center space-x-3">
                  <FaBuilding className="text-gray-400 text-sm" />
                  <div>
                    <span className="text-sm text-gray-500">Company</span>
                    <div className="font-medium text-gray-900">{personalInfo.company}</div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-3">
              <div className="flex items-center space-x-3">
                <FaIdCard className="text-gray-400 text-sm" />
                <div>
                  <span className="text-sm text-gray-500">Customer Number</span>
                  <div className="font-medium text-gray-900">{personalInfo.customerNumber}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <FaGlobe className="text-gray-400 text-sm" />
                <div>
                  <span className="text-sm text-gray-500">Business Model</span>
                  <div className="font-medium text-gray-900">{personalInfo.businessModel}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <FaLanguage className="text-gray-400 text-sm" />
                <div>
                  <span className="text-sm text-gray-500">Preferred Language</span>
                  <div className="font-medium text-gray-900">{personalInfo.preferredLanguage}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <FaDollarSign className="text-gray-400 text-sm" />
                <div>
                  <span className="text-sm text-gray-500">Preferred Currency</span>
                  <div className="font-medium text-gray-900">{personalInfo.preferredCurrency}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <FaMapMarkerAlt className="text-gray-400 text-sm" />
                <div>
                  <span className="text-sm text-gray-500">Preferred Site</span>
                  <div className="font-medium text-gray-900">{personalInfo.preferredSite}</div>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <FaClock className="text-gray-400 text-sm" />
                <div>
                  <span className="text-sm text-gray-500">Last Login</span>
                  <div className="font-medium text-gray-900">{formatDate(personalInfo.lastLogin)}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Addresses */}
      {addresses && addresses.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <FaMapMarkerAlt className="mr-2 text-blue-500" />
            Saved Addresses
          </h3>
          
          <div className="space-y-3">
            {addresses.map((address, index) => (
              <div key={address.addressId || index} className="border border-gray-100 rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-medium text-gray-900">{address.name}</h4>
                      {address.tags && address.tags.includes('default') && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Default
                        </span>
                      )}
                    </div>
                    
                    <div className="text-sm text-gray-700 space-y-1">
                      <div>{address.addressLine1}</div>
                      <div>{address.city}, {address.state} {address.postalCode}</div>
                      <div>{address.country}</div>
                    </div>
                    
                    {address.tags && address.tags.length > 0 && (
                      <div className="flex items-center space-x-2 mt-3">
                        <FaTag className="text-gray-400 text-xs" />
                        <div className="flex flex-wrap gap-1">
                          {address.tags.map((tag, tagIndex) => (
                            <span 
                              key={tagIndex}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountDetailsRenderer;
